"use client";

import { useState, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertCircle, Loader2, Mail, Clock } from "lucide-react";
import { firebaseService } from "@/lib/firebase/client";

// Interfaces
interface FeedbackMessage {
  text: string;
  type: "success" | "error" | "info" | "";
}

interface EmailSubscriptionFormProps {
  buttonText?: string;
  placeholderText?: string;
  className?: string;
  onSuccess?: () => void;
  title?: string;
  subtitle?: string;
  badgeText?: string;
  apiUrl?: string;
}

export function EmailSubscriptionForm({
  buttonText = "Cadastrar",
  placeholderText = "Digite seu email",
  className = "",
  onSuccess,
  title = "🌍 Sua Próxima Aventura Começa Aqui!",
  subtitle = "Deixe seu e-mail e seja avisado assim que o app for lançado!",
  badgeText = "Pré-lançamento",
  apiUrl = "/api/subscribe",
}: EmailSubscriptionFormProps) {
  // Referências e estados
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<FeedbackMessage>({
    text: "",
    type: "",
  });
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [appCheckInitialized, setAppCheckInitialized] = useState(false);

  // Verificar status do App Check
  useEffect(() => {
    try {
      // Verificar o status do App Check periodicamente
      const checkStatus = () => {
        const isInitialized = firebaseService.isAppCheckInitialized();
        if (isInitialized) {
          setAppCheckInitialized(true);
          setRecaptchaLoaded(true);
          console.info("App Check detectado como inicializado");
        }
      };
      
      // Verificar imediatamente
      checkStatus();
      
      // Se não estiver inicializado, tentar inicializar
      if (!firebaseService.isAppCheckInitialized()) {
        console.info("Tentando inicializar App Check...");
        firebaseService.initializeAppCheck();
        
        // Verificar novamente após um breve intervalo
        const timer = setTimeout(checkStatus, 1000);
        return () => clearTimeout(timer);
      }
    } catch (error) {
      console.error("Erro ao verificar App Check:", error);
      setMessage({
        text: "Falha na inicialização do sistema de segurança. Recarregue a página.",
        type: "error",
      });
    }
  }, []);

  // Validação de email
  const validateEmail = (email: string): boolean => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  // Validação completa do formulário
  const validateForm = useCallback((): boolean => {
    if (!email.trim()) {
      setMessage({ text: "Por favor, informe seu email.", type: "error" });
      return false;
    }
    if (!validateEmail(email)) {
      setMessage({
        text: "Por favor, informe um email válido.",
        type: "error",
      });
      return false;
    }
    return true;
  }, [email]);

  // Manipulador de envio do formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if (!validateForm()) return;

    // Iniciar processo de submissão
    setIsLoading(true);
    try {
      await submitEmailToAPI();
    } catch (error) {
      console.error("Erro não tratado ao enviar formulário:", error);
      setMessage({
        text: "Não foi possível processar sua solicitação. Tente novamente mais tarde.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para enviar o email para a API com App Check
  const submitEmailToAPI = async() => {
    try {
      console.info("Obtendo token do Firebase App Check...");
      let appCheckToken;

      try {
        // Obter token usando o serviço centralizado
        appCheckToken = await firebaseService.getAppCheckToken(true);
        console.info("Token App Check obtido com sucesso:", {
          tokenLength: appCheckToken.length,
        });
      } catch (error) {
        console.error("Erro ao obter token do App Check:", error);
        throw new Error(
          "Falha na verificação de segurança. Tente recarregar a página."
        );
      }

      // Fazer requisição para a API
      const startTime = Date.now();
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          // Usar o token do App Check para autenticação
          "X-Firebase-AppCheck": appCheckToken,
        },
        body: JSON.stringify({ email }),
      });

      const responseTime = Date.now() - startTime;
      console.info(`Requisição para ${apiUrl} completada em ${responseTime}ms`);

      // Processar resposta
      const data = await response.json();

      if (!response.ok) {
        console.error("Erro na chamada da API:", {
          status: response.status,
          statusText: response.statusText,
          data,
        });

        // Mensagens específicas para códigos de erro
        if (response.status === 400 && data.code === "INVALID_EMAIL") {
          throw new Error("O email informado parece ser inválido.");
        } else if (response.status === 401) {
          throw new Error(
            "Verificação de segurança falhou. Tente recarregar a página."
          );
        } else if (response.status === 429) {
          throw new Error(
            "Muitas tentativas em um curto período. Aguarde alguns minutos e tente novamente."
          );
        } else {
          throw new Error(data.error || "Erro ao cadastrar seu email.");
        }
      }

      // Sucesso
      setMessage({
        text: data.message || "Email cadastrado com sucesso!",
        type: "success",
      });
      setEmail("");

      // Chamar callback de sucesso se fornecido
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setMessage({
        text:
          error instanceof Error
            ? error.message
            : "Erro ao cadastrar seu email. Tente novamente mais tarde.",
        type: "error",
      });
    }
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6 ${className}`}
    >
      {/* O resto do JSX permanece igual */}
      <div className="mb-6">
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#14B8A6] text-white text-xs font-medium mb-2">
          <Clock className="w-3 h-3 mr-1" /> {badgeText}
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">{subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="sr-only">
            Email
          </Label>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-grow relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholderText}
                className="w-full h-12 rounded-full pl-10 pr-6"
                disabled={isLoading}
                aria-required="true"
                aria-invalid={message.type === "error"}
                aria-describedby={message.text ? "form-feedback" : undefined}
              />
            </div>
            <Button
              type="submit"
              className="h-12 px-8 rounded-full bg-gradient-to-r from-[#3B82F6] to-[#14B8A6] text-white hover:opacity-90 transition-opacity"
              disabled={isLoading || !recaptchaLoaded || !appCheckInitialized}
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                buttonText
              )}
            </Button>
          </div>

          {message.text && (
            <div
              id="form-feedback"
              className={`mt-2 flex items-center text-sm ${
                message.type === "error"
                  ? "text-red-500"
                  : message.type === "success"
                  ? "text-green-500"
                  : "text-blue-500"
              }`}
              role="alert"
            >
              {message.type === "error" ? (
                <AlertCircle className="h-4 w-4 mr-1" />
              ) : message.type === "success" ? (
                <CheckCircle className="h-4 w-4 mr-1" />
              ) : null}
              {message.text}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Fique tranquilo, não enviaremos spam. Você receberá apenas
          notificações sobre o lançamento.
        </p>
      </form>
    </div>
  );
}